import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  FiArrowLeft, 
  FiDownload, 
  FiCheckCircle, 
  FiXCircle, 
  FiClock, 
  FiCalendar, 
  FiFileText, 
  FiPrinter,
  FiShare2,
  FiInfo,
  FiCreditCard,
  FiSmartphone,
  FiBriefcase,
  FiDollarSign,
  FiUpload,
  FiTrash2,
  FiFile
} from 'react-icons/fi';
import { portalAPI, pdfAPI } from '../../services/api';

const formatCurrency = (value) => `${Number(value || 0).toLocaleString('fr-FR')} XOF`;
const formatDate = (date) => date ? new Date(date).toLocaleDateString('fr-FR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric'
}) : '-';

const METHOD_ICONS = {
  'orange-money': FiSmartphone,
  'moov-money': FiSmartphone,
  wave: FiSmartphone,
  'virement-bancaire': FiBriefcase,
  cheque: FiDollarSign,
  'carte-bancaire': FiCreditCard,
  especes: FiDollarSign,
};

const getMethodIcon = (slug) => METHOD_ICONS[slug] || FiCreditCard;

const getStatusConfig = (status) => {
  const statusMap = {
    'paid': { 
      label: 'Payée', 
      color: '#22c55e', 
      bg: '#dcfce7',
      icon: FiCheckCircle 
    },
    'unpaid': { 
      label: 'Non payée', 
      color: '#eab308', 
      bg: '#fef9c3',
      icon: FiClock 
    },
    'overdue': { 
      label: 'En retard', 
      color: '#ef4444', 
      bg: '#fee2e2',
      icon: FiXCircle 
    },
    'pending': { 
      label: 'En attente', 
      color: '#3b82f6', 
      bg: '#dbeafe',
      icon: FiClock 
    },
    'cancelled': { 
      label: 'Annulée', 
      color: '#6b7280', 
      bg: '#f3f4f6',
      icon: FiXCircle 
    },
    'sent': { 
      label: 'Envoyée', 
      color: '#8b5cf6', 
      bg: '#ede9fe',
      icon: FiInfo 
    },
  };
  return statusMap[status] || statusMap['pending'];
};

// ─── COMPOSANT UPLOAD REÇU UNIVERSEL ───
const ReceiptUpload = ({ receiptImage, setReceiptImage }) => {
  const [dragOver, setDragOver] = useState(false);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) setReceiptImage(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) setReceiptImage(file);
  };

  const handleDragOver = (e) => { e.preventDefault(); setDragOver(true); };
  const handleDragLeave = () => setDragOver(false);

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (receiptImage) {
    const isImage = receiptImage.type?.startsWith('image/');
    const previewUrl = isImage ? URL.createObjectURL(receiptImage) : null;

    return (
      <div style={{
        background: '#f0fdf4',
        border: '1px solid #86efac',
        borderRadius: 10,
        padding: 16,
        marginTop: 16
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {isImage && previewUrl ? (
            <img src={previewUrl} alt="Reçu" style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 8 }} />
          ) : (
            <div style={{
              width: 56, height: 56, background: '#fef3c7', borderRadius: 8,
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <FiFile size={24} color="#d97706" />
            </div>
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {receiptImage.name}
            </div>
            <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
              {formatFileSize(receiptImage.size)} · {receiptImage.type || 'Fichier'}
            </div>
          </div>
          <button
            onClick={() => setReceiptImage(null)}
            style={{
              background: '#fee2e2', color: '#ef4444', border: 'none',
              borderRadius: 6, padding: '6px 10px', fontSize: 12,
              cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4
            }}
          >
            <FiTrash2 size={14} /> Retirer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ marginTop: 16 }}>
      <label style={{ fontSize: 13, color: '#475569', fontWeight: 500, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
        <FiUpload size={14} />
        Reçu / Preuve de paiement <span style={{ color: '#94a3b8', fontWeight: 400 }}>(optionnel)</span>
      </label>
      <div
        onClick={() => document.getElementById('receiptInputInvoice').click()}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        style={{
          border: `2px dashed ${dragOver ? '#3b82f6' : '#cbd5e1'}`,
          borderRadius: 10,
          padding: '24px 16px',
          textAlign: 'center',
          cursor: 'pointer',
          background: dragOver ? '#eff6ff' : 'white',
          transition: 'all 0.2s'
        }}
      >
        <div style={{ fontSize: 32, marginBottom: 8 }}>📤</div>
        <div style={{ fontSize: 13, color: '#475569', fontWeight: 500 }}>Cliquez ou glissez un fichier ici</div>
        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>Images (JPG, PNG) ou PDF — max 5 Mo</div>
      </div>
      <input
        id="receiptInputInvoice"
        type="file"
        accept="image/*,.pdf"
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />
    </div>
  );
};

const PortalInvoiceDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isDownloading, setIsDownloading] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);

  // ─── ÉTATS PAIEMENT UNIFIÉS ───
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [paymentType, setPaymentType] = useState('single');
  const [installmentType, setInstallmentType] = useState('half');
  const [customInstallmentAmount, setCustomInstallmentAmount] = useState('');
  const [selectedMethod, setSelectedMethod] = useState('');
  const [selectedMethodDetails, setSelectedMethodDetails] = useState(null);
  const [showManualForm, setShowManualForm] = useState(false);
  const [manualReference, setManualReference] = useState('');
  const [manualNotes, setManualNotes] = useState('');
  const [processing, setProcessing] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState('pending');
  const [calculatedAmount, setCalculatedAmount] = useState(0);

  // ─── REÇU UNIVERSEL (optionnel pour TOUTES les méthodes) ───
  const [receiptImage, setReceiptImage] = useState(null);

  // ─── CHARGER LES MÉTHODES DE PAIEMENT DE L'AGENCE ───
  useEffect(() => {
    const fetchMethods = async () => {
      try {
        const { data } = await portalAPI.billing.getAgencyPaymentMethods();
        const methods = Array.isArray(data) ? data : [];
        setPaymentMethods(methods);

        if (methods.length > 0 && !selectedMethod) {
          setSelectedMethod(methods[0].payment_method_id);
          setSelectedMethodDetails(methods[0]);
          const manualMethods = ['virement-bancaire', 'cheque', 'especes'];
          setShowManualForm(manualMethods.includes(methods[0].slug));
        }
      } catch (e) {
        console.warn('Cannot load payment methods:', e);
      }
    };
    fetchMethods();
  }, []);

  // ─── RECALCULER LE MONTANT SELON LE TYPE ───
  useEffect(() => {
    if (!invoice) return;
    const baseAmount = parseFloat(invoice.total);

    if (paymentType === 'single') {
      setCalculatedAmount(baseAmount);
    } else if (paymentType === 'installment') {
      if (installmentType === 'half') {
        setCalculatedAmount(baseAmount / 2);
      } else {
        const val = parseFloat(customInstallmentAmount) || 0;
        setCalculatedAmount(val);
      }
    } else if (paymentType === 'advance') {
      setCalculatedAmount(baseAmount);
    }
  }, [paymentType, installmentType, customInstallmentAmount, invoice]);

  // ─── FONCTION PAIEMENT FACTURE UNIFIÉE ───
  // const handlePayInvoice = async () => {
  //   if (!selectedMethod) {
  //     alert('Veuillez sélectionner une méthode de paiement');
  //     return;
  //   }

  //   if (paymentType === 'installment') {
  //     const baseAmount = parseFloat(invoice.total);
  //     if (calculatedAmount <= 0 || calculatedAmount >= baseAmount) {
  //       alert(`Le montant de la tranche doit être supérieur à 0 et inférieur à ${formatCurrency(baseAmount)}`);
  //       return;
  //     }
  //   }

  //   setProcessing(true);

  //   try {
  //     const providerSlug = selectedMethodDetails?.slug || 'orange_money';
  //     const manualMethods = ['virement-bancaire', 'cheque', 'especes'];
  //     const isManual = manualMethods.includes(providerSlug);

  //     // ─── CONSTRUIRE LE FORMDATA AVEC REÇU OPTIONNEL ───
  //     const formData = new FormData();
  //     formData.append('invoice_id', id);
  //     formData.append('payment_method_id', selectedMethod);
  //     formData.append('type', paymentType);

  //     if (paymentType === 'installment') {
  //       formData.append('installment_amount', calculatedAmount);
  //     } else if (paymentType === 'advance') {
  //       formData.append('advance_amount', calculatedAmount);
  //     }

  //     // ⬇️ AJOUT DU REÇU OPTIONNEL (pour TOUTES les méthodes)
  //     if (receiptImage) {
  //       formData.append('file', receiptImage);
  //     }

  //     if (isManual) {
  //       // ─── PAIEMENT MANUEL ───
  //       formData.append('reference', manualReference);
  //       formData.append('notes', manualNotes);
  //       formData.append('amount', calculatedAmount);

  //       await portalAPI.billing.submitManualInvoicePayment(id, formData);
  //     } else {
  //       // ─── PAIEMENT AUTOMATIQUE (OM, Wave, etc.) ───
  //       formData.append('provider', providerSlug);

  //       await portalAPI.billing.checkoutLeasePayment(formData);
  //     }

  //     setPaymentStatus('initiated');

  //     // Recharger la facture pour voir le statut mis à jour
  //     const { data } = await portalAPI.billing.getInvoiceById(id);
  //     setInvoice(data);

  //   } catch (err) {
  //     setPaymentStatus('failed');
  //     alert(err.response?.data?.error || 'Erreur lors du paiement');
  //   } finally {
  //     setProcessing(false);
  //   }
  // };
     // ─── FONCTION PAIEMENT FACTURE UNIFIÉE ───
// ─── FONCTION PAIEMENT FACTURE UNIFIÉE ───
const handlePayInvoice = async () => {
  if (!selectedMethod) {
    alert('Veuillez sélectionner une méthode de paiement');
    return;
  }

  if (paymentType === 'installment') {
    const baseAmount = parseFloat(invoice.total);
    if (calculatedAmount <= 0 || calculatedAmount >= baseAmount) {
      alert(`Le montant de la tranche doit être supérieur à 0 et inférieur à ${formatCurrency(baseAmount)}`);
      return;
    }
  }

  setProcessing(true);

  try {
    const providerSlug = selectedMethodDetails?.slug || 'orange_money';
    const manualMethods = ['virement-bancaire', 'cheque', 'especes'];
    const isManual = manualMethods.includes(providerSlug);

    // ─── CONSTRUIRE LE FORMDATA ───
    const formData = new FormData();
    // ❌ NE PAS METTRE : formData.append('invoice_id', id);
    // L'invoice_id est déjà dans l'URL : /invoices/${id}/pay
    
    formData.append('payment_method_id', selectedMethod);
    formData.append('type', paymentType);

    if (paymentType === 'installment') {
      formData.append('installment_amount', calculatedAmount);
    } else if (paymentType === 'advance') {
      formData.append('advance_amount', calculatedAmount);
    }

    // ⬇️ AJOUT DU REÇU OPTIONNEL
    if (receiptImage) {
      formData.append('file', receiptImage);
    }

    if (isManual) {
      // ─── PAIEMENT MANUEL ───
      formData.append('reference', manualReference);
      formData.append('notes', manualNotes);
      formData.append('amount', calculatedAmount);

      await portalAPI.billing.submitManualInvoicePayment(id, formData);
    } else {
      // ─── PAIEMENT AUTOMATIQUE (OM, Wave, etc.) ───
      formData.append('provider', providerSlug);

      // ❌ AVANT : await portalAPI.billing.checkoutLeasePayment(formData);
      // ✅ CORRIGÉ : Utiliser payInvoice avec l'id
      await portalAPI.billing.payInvoice(id, formData);
    }

    setPaymentStatus('initiated');

    // Recharger la facture
    const { data } = await portalAPI.billing.getInvoiceById(id);
    setInvoice(data);

  } catch (err) {
    setPaymentStatus('failed');
    alert(err.response?.data?.error || 'Erreur lors du paiement');
  } finally {
    setProcessing(false);
  }
};
  useEffect(() => {
    const fetchInvoice = async () => {
      try {
        const { data } = await portalAPI.billing.getInvoiceById(id);
        setInvoice(data);
        setCalculatedAmount(parseFloat(data.total));
        console.log('📄 InvoicePortal:', data);
      } catch (err) {
        console.error('❌ Erreur chargement facture:', err);
        alert('Erreur lors du chargement de la facture');
        navigate('/portal/invoices');
      } finally {
        setLoading(false);
      }
    };
    fetchInvoice();
  }, [id, navigate]);

  const downloadPDF = async () => {
    try {
      setIsDownloading(true);
      const { data } = await pdfAPI.downloadInvoice(id);
      const url = window.URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `facture-${invoice?.invoice_number || id}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('❌ Erreur téléchargement PDF:', err);
      alert('Erreur lors du téléchargement du PDF');
    } finally {
      setIsDownloading(false);
    }
  };

  const handlePrint = () => { window.print(); };

  const handleShare = () => {
    const url = window.location.href;
    const text = `Facture ${invoice?.invoice_number} - ${formatCurrency(invoice?.total)}`;
    if (navigator.share) {
      navigator.share({ title: `Facture ${invoice?.invoice_number}`, text, url }).catch(() => {});
    } else {
      navigator.clipboard.writeText(url).then(() => alert('Lien copié dans le presse-papier !')).catch(() => setShowShareMenu(!showShareMenu));
    }
  };

  if (loading) {
    return (
      <div className="loading" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <div className="spinner" style={{ width: 40, height: 40, border: '4px solid var(--border)', borderTop: '4px solid var(--primary)', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
      </div>
    );
  }

  if (!invoice) return null;

  const statusConfig = getStatusConfig(invoice.status);

  // ─── RENDU DE LA SECTION PAIEMENT ───
  const renderPaymentSection = () => {
    if (invoice.status === 'paid' || invoice.status === 'cancelled') return null;

    if (paymentStatus === 'initiated') {
      return (
        <div className="card" style={{ marginTop: 20, borderRadius: 12, textAlign: 'center', padding: 40 }}>
          <FiClock size={64} color="#f59e0b" style={{ marginBottom: 20 }} />
          <h2>Demande soumise avec succès</h2>
          <div style={{
            background: '#fffbeb',
            border: '1px solid #fcd34d',
            borderRadius: 8,
            padding: '16px 20px',
            margin: '24px 0',
            textAlign: 'left'
          }}>
            <p style={{ margin: 0, color: '#92400e' }}>
              <strong>Votre demande de paiement a été transmise à l'agence.</strong>
            </p>
            <p style={{ margin: '8px 0 0', color: '#92400e', fontSize: 14 }}>
              L'agence va vérifier et valider votre paiement sous peu. 
              Vous recevrez une notification dès que la validation sera effectuée.
            </p>
          </div>
          <div style={{ background: 'var(--bg-secondary)', padding: 20, borderRadius: 8, margin: '24px 0', textAlign: 'left' }}>
            <div className="detail-row">
              <span className="detail-label">Facture:</span>
              <span className="detail-value">{invoice.invoice_number}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Montant:</span>
              <span className="detail-value" style={{ color: 'var(--primary-color)' }}>
                {formatCurrency(calculatedAmount)}
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Type:</span>
              <span className="detail-value">
                {paymentType === 'single' ? 'Paiement complet' : 
                 paymentType === 'installment' ? 'Paiement par tranche' : 
                 'Paiement par avance'}
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Méthode:</span>
              <span className="detail-value">{selectedMethodDetails?.name || '-'}</span>
            </div>
          </div>
          <button className="btn btn-primary" onClick={() => navigate('/portal/invoices')}>
            Retour aux factures
          </button>
        </div>
      );
    }

    if (paymentStatus === 'failed') {
      return (
        <div className="card" style={{ marginTop: 20, borderRadius: 12, textAlign: 'center', padding: 40 }}>
          <FiXCircle size={64} color="red" style={{ marginBottom: 20 }} />
          <h2>Échec du paiement</h2>
          <p style={{ color: 'var(--text-secondary)', margin: '16px 0' }}>
            Une erreur est survenue lors du traitement de votre paiement.
          </p>
          <button className="btn btn-secondary" onClick={() => setPaymentStatus('pending')} style={{ marginRight: 12 }}>
            Réessayer
          </button>
          <button className="btn btn-primary" onClick={() => navigate('/portal/invoices')}>
            Retour aux factures
          </button>
        </div>
      );
    }

    return (
      <div className="card" style={{ marginTop: 20, borderRadius: 12, overflow: 'hidden' }}>
        <div className="card-header" style={{ padding: '16px 24px', borderBottom: '1px solid var(--border)' }}>
          <h2 style={{ margin: 0, fontSize: 18, display: 'flex', alignItems: 'center', gap: 8 }}>
            <FiCreditCard size={20} color="var(--primary)" />
            Payer cette facture
          </h2>
        </div>
        <div className="card-body" style={{ padding: 24 }}>

          {/* Types de paiement */}
          <div style={{ marginBottom: 20 }}>
            <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 8 }}>
              Type de paiement
            </p>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button
                className={`btn ${paymentType === 'single' ? 'btn-primary' : 'btn-outline'}`}
                onClick={() => setPaymentType('single')}
                style={{ flex: 1, minWidth: 100 }}
              >
                Paiement complet
              </button>
              <button
                className={`btn ${paymentType === 'installment' ? 'btn-primary' : 'btn-outline'}`}
                onClick={() => setPaymentType('installment')}
                style={{ flex: 1, minWidth: 100 }}
              >
                Tranche
              </button>
              <button
                className={`btn ${paymentType === 'advance' ? 'btn-primary' : 'btn-outline'}`}
                onClick={() => setPaymentType('advance')}
                style={{ flex: 1, minWidth: 100 }}
              >
                Payer en avance
              </button>
            </div>

            {/* Options de tranche */}
            {paymentType === 'installment' && (
              <div style={{ marginTop: 16, borderTop: '1px solid var(--border-color)', paddingTop: 12 }}>
                <label style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 8, display: 'block' }}>
                  Option de tranche :
                </label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    type="button"
                    className={`btn ${installmentType === 'half' ? 'btn-primary' : 'btn-outline'}`}
                    onClick={() => setInstallmentType('half')}
                    style={{ flex: 1 }}
                  >
                    Moitié (50%)
                  </button>
                  <button
                    type="button"
                    className={`btn ${installmentType === 'custom' ? 'btn-primary' : 'btn-outline'}`}
                    onClick={() => setInstallmentType('custom')}
                    style={{ flex: 1 }}
                  >
                    Montant personnalisé
                  </button>
                </div>
                {installmentType === 'custom' && (
                  <div style={{ marginTop: 12 }}>
                    <label style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                      Montant de la tranche (XOF) :
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      style={{ marginTop: 6 }}
                      value={customInstallmentAmount}
                      onChange={(e) => setCustomInstallmentAmount(e.target.value)}
                      placeholder="ex: 50000"
                      min="1"
                      max={parseFloat(invoice.total) - 1}
                    />
                    <small style={{ color: 'var(--text-secondary)', marginTop: 4, display: 'block' }}>
                      Le montant doit être inférieur à {formatCurrency(parseFloat(invoice.total))}
                    </small>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Méthodes de paiement */}
          <div style={{ marginBottom: 20 }}>
            <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 8 }}>
              Méthode de paiement
            </p>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {paymentMethods.map(method => {
                const Icon = getMethodIcon(method.slug);
                return (
                  <button
                    key={method.payment_method_id}
                    className={`btn ${selectedMethod === method.payment_method_id ? 'btn-primary' : 'btn-outline'}`}
                    onClick={() => {
                      setSelectedMethod(method.payment_method_id);
                      setSelectedMethodDetails(method);
                      const manualMethods = ['virement-bancaire', 'cheque', 'especes'];
                      setShowManualForm(manualMethods.includes(method.slug));
                    }}
                    style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                    title={method.details?.instructions || method.description}
                  >
                    <Icon size={16} />
                    {method.name}
                  </button>
                );
              })}
            </div>

            {/* Détails de la méthode */}
            {selectedMethodDetails && (
              <div style={{ marginTop: 12, padding: 12, background: 'white', borderRadius: 6, border: '1px solid var(--border)' }}>
                {selectedMethodDetails.details && Object.keys(selectedMethodDetails.details).length > 0 && (
                  <div style={{ fontSize: 13 }}>
                    {Object.entries(selectedMethodDetails.details).map(([key, value]) => (
                      <div key={key} style={{ marginBottom: 4 }}>
                        <strong style={{ textTransform: 'capitalize' }}>{key.replace(/_/g, ' ')}:</strong> {value}
                      </div>
                    ))}
                  </div>
                )}
                {selectedMethodDetails.details?.instructions && (
                  <div style={{ marginTop: 8, padding: 8, background: '#f8fafc', borderRadius: 4, fontSize: 12, color: 'var(--text-secondary)' }}>
                    <FiInfo style={{ marginRight: 4 }} />
                    {selectedMethodDetails.details.instructions}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Formulaire manuel (uniquement si méthode manuelle) */}
          {showManualForm && (
            <div style={{ background: 'var(--bg-secondary)', padding: 16, borderRadius: 8, marginBottom: 20 }}>
              <h4 style={{ margin: '0 0 12px', fontSize: 14 }}>Détails du paiement manuel</h4>
              <div className="form-group" style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 13 }}>Référence du paiement</label>
                <input
                  type="text"
                  className="form-control"
                  value={manualReference}
                  onChange={(e) => setManualReference(e.target.value)}
                  placeholder="ex: VIREMENT-123456"
                />
              </div>
              <div className="form-group" style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 13 }}>Notes (optionnel)</label>
                <textarea
                  className="form-control"
                  rows="2"
                  value={manualNotes}
                  onChange={(e) => setManualNotes(e.target.value)}
                  placeholder="Informations supplémentaires..."
                />
              </div>
            </div>
          )}

          {/* ─── UPLOAD REÇU UNIVERSEL (TOUTES les méthodes) ─── */}
          <ReceiptUpload receiptImage={receiptImage} setReceiptImage={setReceiptImage} />

          {/* Montant et bouton */}
          <hr style={{ margin: '16px 0', borderTop: '1px solid var(--border-color)' }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
            <div>
              <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)' }}>
                Montant à payer
              </p>
              <p style={{ margin: 0, fontSize: 24, fontWeight: 700, color: 'var(--primary)' }}>
                {formatCurrency(calculatedAmount)}
              </p>
            </div>
            <button
              className="btn btn-primary"
              style={{ padding: '12px 32px', fontSize: 16 }}
              onClick={handlePayInvoice}
              disabled={processing || !selectedMethod}
            >
              <FiCreditCard style={{ marginRight: 8 }} />
              {processing ? 'Traitement...' : showManualForm ? 'Soumettre le paiement' : 'Payer maintenant'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="portal-invoice-detail" style={{ maxWidth: 900, margin: '0 auto', padding: '16px 0' }}>
      {/* Header */}
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <button 
            className="btn btn-secondary btn-sm" 
            onClick={() => navigate('/portal/invoices')} 
            style={{ marginBottom: 12 }}
          >
            <FiArrowLeft /> Retour aux factures
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <h1 style={{ margin: 0 }}>Facture {invoice.invoice_number}</h1>
            <span 
              className="badge" 
              style={{ 
                background: statusConfig.bg, 
                color: statusConfig.color,
                padding: '6px 16px',
                borderRadius: 20,
                fontWeight: 600,
                fontSize: 14,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6
              }}
            >
              <statusConfig.icon size={16} />
              {statusConfig.label}
            </span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button 
            className="btn btn-primary btn-sm" 
            onClick={downloadPDF} 
            disabled={isDownloading}
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
          >
            <FiDownload size={16} /> 
            {isDownloading ? 'Téléchargement...' : 'PDF'}
          </button>
          <button 
            className="btn btn-secondary btn-sm" 
            onClick={handlePrint}
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
          >
            <FiPrinter size={16} /> Imprimer
          </button>
          <button 
            className="btn btn-secondary btn-sm" 
            onClick={handleShare}
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
          >
            <FiShare2 size={16} /> Partager
          </button>
        </div>
      </div>

      {/* Informations de la facture */}
      <div className="card" style={{ marginTop: 20, borderRadius: 12, overflow: 'hidden' }}>
        <div className="card-body" style={{ padding: 24 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 20 }}>
            <div>
              <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 4 }}>Destinataire</p>
              <p style={{ fontWeight: 600, margin: 0 }}>
                {invoice.counterparty_name || 'Locataire'}
              </p>
              {invoice.property_title && (
                <p style={{ color: 'var(--text-secondary)', fontSize: 13, margin: '4px 0 0 0' }}>
                  {invoice.property_title}
                </p>
              )}
              {invoice.property_address && (
                <p style={{ color: 'var(--text-secondary)', fontSize: 12, margin: '2px 0 0 0' }}>
                  {invoice.property_address}
                </p>
              )}
            </div>

            <div>
              <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 4 }}>Date d'émission</p>
              <p style={{ fontWeight: 600, margin: 0, display: 'flex', alignItems: 'center', gap: 6 }}>
                <FiCalendar size={14} color="var(--text-secondary)" />
                {formatDate(invoice.issue_date)}
              </p>
            </div>
            <div>
              <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 4 }}>Date d'échéance</p>
              <p style={{ fontWeight: 600, margin: 0, display: 'flex', alignItems: 'center', gap: 6 }}>
                <FiCalendar size={14} color="var(--text-secondary)" />
                {formatDate(invoice.due_date)}
              </p>
            </div>

            <div>
              <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 4 }}>Montant total</p>
              <p style={{ 
                fontWeight: 700, 
                fontSize: 20, 
                margin: 0, 
                color: 'var(--primary)',
                display: 'flex',
                alignItems: 'center',
                gap: 6
              }}>
                {formatCurrency(invoice.total)}
                {invoice.status === 'paid' && (
                  <FiCheckCircle size={20} color="#22c55e" />
                )}
              </p>
            </div>

            {invoice.status === 'paid' && (
              <div>
                <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 4 }}>Date de paiement</p>
                <p style={{ fontWeight: 600, margin: 0, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <FiCheckCircle size={14} color="#22c55e" />
                  {formatDate(invoice.paid_date)}
                </p>
              </div>
            )}
          </div>

          {invoice.notes && (
            <div style={{ marginTop: 16, padding: 12, background: 'var(--bg)', borderRadius: 8 }}>
              <p style={{ color: 'var(--text-secondary)', fontSize: 13, margin: 0 }}>
                <strong>Notes :</strong> {invoice.notes}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Lignes de la facture */}
      <div className="card" style={{ marginTop: 20, borderRadius: 12, overflow: 'hidden' }}>
        <div className="card-header" style={{ padding: '16px 24px', borderBottom: '1px solid var(--border)' }}>
          <h2 style={{ margin: 0, fontSize: 18 }}>Détails de la facture</h2>
        </div>
        <div className="card-body" style={{ padding: 0 }}>
          <div className="table-responsive" style={{ overflowX: 'auto' }}>
            <table className="table" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--bg)', borderBottom: '2px solid var(--border)' }}>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600 }}>Description</th>
                  <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 600 }}>Qté</th>
                  <th style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 600 }}>Prix unitaire</th>
                  <th style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 600 }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {invoice.items && invoice.items.length > 0 ? (
                  invoice.items.map((item, idx) => (
                    <tr key={idx} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ padding: '12px 16px' }}>{item.description}</td>
                      <td style={{ padding: '12px 16px', textAlign: 'center' }}>{item.quantity || 1}</td>
                      <td style={{ padding: '12px 16px', textAlign: 'right' }}>{formatCurrency(item.unit_price)}</td>
                      <td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 600 }}>
                        {formatCurrency(item.total || (item.quantity || 1) * item.unit_price)}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" style={{ padding: '24px', textAlign: 'center', color: 'var(--text-secondary)' }}>
                      <FiFileText size={24} style={{ display: 'block', margin: '0 auto 8px' }} />
                      Aucune ligne trouvée
                    </td>
                  </tr>
                )}
              </tbody>
              <tfoot>
                <tr style={{ borderTop: '2px solid var(--border)' }}>
                  <td colSpan="3" style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 600 }}>Sous-total</td>
                  <td style={{ padding: '12px 16px', textAlign: 'right' }}>{formatCurrency(invoice.subtotal)}</td>
                </tr>
                {Number(invoice.tax_rate) > 0 && (
                  <tr>
                    <td colSpan="3" style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 600 }}>
                      TVA ({invoice.tax_rate}%)
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'right' }}>{formatCurrency(invoice.tax_amount)}</td>
                  </tr>
                )}
                <tr style={{ background: 'var(--bg)', fontWeight: 700 }}>
                  <td colSpan="3" style={{ padding: '12px 16px', textAlign: 'right', fontSize: 16 }}>Total</td>
                  <td style={{ padding: '12px 16px', textAlign: 'right', fontSize: 18, color: 'var(--primary)' }}>
                    {formatCurrency(invoice.total)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>

      {/* Historique des paiements */}
      {invoice.payments && invoice.payments.length > 0 && (
        <div className="card" style={{ marginTop: 20, borderRadius: 12, overflow: 'hidden' }}>
          <div className="card-header" style={{ padding: '16px 24px', borderBottom: '1px solid var(--border)' }}>
            <h2 style={{ margin: 0, fontSize: 18 }}>Historique des paiements</h2>
          </div>
          <div className="card-body" style={{ padding: 0 }}>
            <div className="table-responsive" style={{ overflowX: 'auto' }}>
              <table className="table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: 'var(--bg)', borderBottom: '2px solid var(--border)' }}>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600 }}>Date</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600 }}>Méthode</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600 }}>Référence</th>
                    <th style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 600 }}>Montant</th>
                    <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 600 }}>Statut</th>
                  </tr>
                </thead>
                <tbody>
                  {invoice.payments.map((payment, idx) => (
                    <tr key={idx} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ padding: '12px 16px' }}>{formatDate(payment.payment_date || payment.created_at)}</td>
                      <td style={{ padding: '12px 16px' }}>{payment.payment_method_name || '-'}</td>
                      <td style={{ padding: '12px 16px' }}>{payment.reference || '-'}</td>
                      <td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 600 }}>
                        {formatCurrency(payment.amount)}
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                        <span 
                          style={{ 
                            background: payment.status === 'completed' ? '#dcfce7' : '#fef9c3',
                            color: payment.status === 'completed' ? '#22c55e' : '#eab308',
                            padding: '2px 12px',
                            borderRadius: 12,
                            fontSize: 12,
                            fontWeight: 600
                          }}
                        >
                          {payment.status === 'completed' ? 'Validé' : 'En attente'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ─── SECTION PAIEMENT UNIFIÉE ─── */}
      {renderPaymentSection()}

      {/* Actions */}
      <div style={{ marginTop: 24, display: 'flex', justifyContent: 'flex-end', gap: 12, padding: '0 4px' }}>
        <button 
          className="btn btn-secondary" 
          onClick={() => navigate('/portal/invoices')}
        >
          <FiArrowLeft /> Retour
        </button>
        <button 
          className="btn btn-primary" 
          onClick={downloadPDF} 
          disabled={isDownloading}
        >
          <FiDownload /> {isDownloading ? 'Téléchargement...' : 'Télécharger PDF'}
        </button>
      </div>

      {/* Styles CSS pour l'impression */}
      <style>{`
        @media print {
          .page-header button, 
          .page-header .btn,
          .card-header button,
          .card-header .btn,
          .portal-invoice-detail > div:last-child {
            display: none !important;
          }
          .card {
            border: 1px solid #ddd !important;
            break-inside: avoid;
          }
          body {
            background: white !important;
          }
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default PortalInvoiceDetailPage;